import { SortDirection } from "@bridgesplit/utils";
import { ParsedTransactionInstruction, BaseRouteStep } from "@loopscale/pandora";

import { ExpectedLoanValues, StrategyDuration } from "../types";

export interface LoopVaultInfoExpanded extends LoopVaultInfo {
    maturityDate?: Date;
    protocols?: Protocol[];
    assetTagType?: LoopAssetTypeTag[];
    targetRedemptionMint?: string;
}

export interface LoopVaultInfo {
    collateralMint: string;
    principalMint: string;
    collateralDeposited: number;
    collateralApyPct: number;
    maxLeverage: number;
    name: string;
    maxLeveragedApyPct: number;
    metadata?: MeteoraLoopMetadata | null;
    principalAmountAvailable: number;
    routeType?: LoopRouteType | null;
    tags?: LoopFilterTags[];
    quoteFetchParams: QuoteFetchParams;
    maxCollateralAmount?: number;
    aumLimit?: number;
    feVisible: boolean;
    wAvgLeverage?: number;
    wAvgApy?: number;
    oneWeekCollateralDeposits?: number; //U64
    creationTime?: number;
    featured?: boolean;
}

// This will create a union type that preserves the discriminated union structure
// but removes the specified properties from each variant
export type QuoteFetchParams = {
    [K in BaseRouteStep["type"]]: Omit<Extract<BaseRouteStep, { type: K }>, "inputMint" | "outputMint"> & {
        targetRedemptionMint?: string;
    };
}[BaseRouteStep["type"]];

export interface JupQuoteFetchParams {
    onlyDirectRoutes?: boolean;
    maxAccounts?: number;
    dexes?: string[];
}

export interface MeteoraLoopMetadata {
    aTokenVault: string;
    bTokenVault: string;
    lpMint: string;
    lutAddress: string;
    lutAddresses: string;
    poolAddress: string;
    tokenAMint: string;
    tokenBMint: string;
    vaultA: string;
    vaultALp: string;
    vaultALpMint: string;
    vaultB: string;
    vaultBLp: string;
    vaultBLpMint: string;
}
/**
 * Loop tag enums for categorizing and filtering loop vaults. They are the JSON tags column returned from `loop_vaults` table
 *
 * Protocol: Used for filtering loops by their underlying protocol (e.g., Meteora, Exponent)
 * LoopFilterTags: Used for filtering loops by their characteristics (e.g., SOL-pegged, DEX LP)
 * LoopAssetTypeTag: Used to determine asset types for loops, particularly for old chart functionality (e.g., LST, LRT)
 */

export enum Protocol {
    Meteora = "Meteora",
    Exponent = "Exponent",
    RateX = "RateX",
    Sanctum = "Sanctum",
    FlashTrade = "Flash.Trade",
    Fragmetric = "Fragmetric",
    Adrastea = "Adrastea",
    Stabble = "Stabble",
    Solayer = "Solayer"
}

export enum LoopFilterTags {
    SolPegged = "SOL-Pegged",
    StablecoinPegged = "Stablecoin-Pegged",
    DexLp = "DEX LP",
    FixedYield = "Fixed Yield",
    Farming = "Farming",
    Rwa = "RWA",
    Directional = "Directional"
}

export enum LoopAssetTypeTag {
    LST = "LST",
    LRT = "LRT"
}

export type LoopTag = LoopFilterTags | Protocol | LoopAssetTypeTag;

export enum LoopRouteType {
    Jup,
    Meteora,
    Stabble,
    Exponent,
    Flash,
    Adrena,
    Ratex
}

export interface LoopInfoParams {
    loopVaults?: string[];
    showHidden: boolean;
}

export interface UserLoopFilter {
    principalMint?: string;
    collateralMint?: string;
    active: boolean;
    sortDirection?: SortDirection;
    sortType?: LoopPositionSortType;
    page: number;
    pageSize: number;
}

export enum LoopPositionSortType {
    StartTime,
    LeverageMultiplier,
    NetApy,
    NetPositionValue,
    HealthRatio
}

export interface UserLoopInfoParams extends UserLoopFilter {
    loopVaults?: string[];
    loanVaults?: string[];
}

export interface LoopYieldParams {
    collateral: string[];
}

export interface LoopWindTransactionInput {
    vaultIdentifier: string;
    leverageMultiplier: number;
    strategyAddress: string;
    strategyDuration: StrategyDuration;
    collateralAmount: number;
    principalRequested: number;
    expectedStartingLtv: number;
    expectedLoanValues: ExpectedLoanValues;
    swapIx: ParsedTransactionInstruction[];
    swapLuts: string[];
}

export interface LoopUnwindTransactionInput {
    loanAddress: string;
    principalMint: string;
    collateralMint: string;
    swapIx: ParsedTransactionInstruction[];
    swapLuts: string[];
    unwindType: LoopUnwindType;
}

export enum LoopUnwindType {
    Collateral = 0,
    Principal = 1
}

export const DEFAULT_UNWIND_TYPE = LoopUnwindType.Collateral;

export interface UserLoopInfoExpanded extends UserLoopInfo {
    netApyLive?: number;
}

export interface WithdrawalInfo {
    withdrawnTokenMint: string;
    withdrawnTokenAmount: number;
}

export interface UserLoopInfo {
    loanAddress: string;
    leverageMultiplier: number;
    initialCollateralAmount: number;
    loopVaultIdentifier: string;
    netApy?: number; // cached server net apy. price and time values can be stale
    positionStartTime: number;
    healthRatio?: number;
    netPositionValue: number;
    index: number;
    totalCount: number;
    collateralYieldPct: number;
    principalYieldPct: number;
    withdrawalInfo?: WithdrawalInfo; // amount withdrawn on loop close
}
